import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AdminDashboard from "@/pages/AdminDashboard";
import CompanyDashboard from "@/pages/CompanyDashboard";
import ClaimsList from "@/pages/ClaimsList";
import ClaimForm from "@/pages/ClaimForm";
import ClaimDetail from "@/pages/ClaimDetail";
import SendReport from "@/pages/SendReport";
// WorkOrders module disabled — unused by clients, causes confusion
// import WorkOrders from "@/pages/WorkOrders";
import CompanyUsers from "@/pages/CompanyUsers";
import AdminCompanies from "@/pages/AdminCompanies";
import AdminUsers from "@/pages/AdminUsers";
import MyAccount from "@/pages/MyAccount";
import HelpSupport from "@/pages/HelpSupport";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import LegalNotice from "@/pages/LegalNotice";
import LegalFooter from "@/components/LegalFooter";

// Context
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { OfflineProvider, useOffline } from "@/context/OfflineContext";

// Components
import MobileNav from "@/components/MobileNav";
import OfflineBanner from "@/components/OfflineBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect based on role
    if (user.role === "admin") {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppContent() {
  const { user } = useAuth();
  const { isOnline } = useOffline();

  return (
    <div className="App min-h-screen bg-slate-50">
      {!isOnline && <OfflineBanner />}
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        
        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/companies"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminCompanies />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminUsers />
            </ProtectedRoute>
          }
        />

        {/* Company/Technician Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedRoles={["company", "technician"]}>
              <CompanyDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/claims"
          element={
            <ProtectedRoute allowedRoles={["company", "technician", "observer"]}>
              <ClaimsList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/claims/new"
          element={
            <ProtectedRoute allowedRoles={["company", "technician"]}>
              <ClaimForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/claims/:id"
          element={
            <ProtectedRoute allowedRoles={["company", "technician", "observer"]}>
              <ClaimDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/claims/:id/edit"
          element={
            <ProtectedRoute allowedRoles={["company", "technician"]}>
              <ClaimForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/claims/:id/send"
          element={
            <ProtectedRoute allowedRoles={["company", "technician"]}>
              <SendReport />
            </ProtectedRoute>
          }
        />
        {/* WorkOrders route disabled — module unused, causes confusion
        <Route
          path="/work-orders"
          element={
            <ProtectedRoute allowedRoles={["company", "technician"]}>
              <WorkOrders />
            </ProtectedRoute>
          }
        />
        */}
        <Route
          path="/users"
          element={
            <ProtectedRoute allowedRoles={["company"]}>
              <CompanyUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account"
          element={
            <ProtectedRoute allowedRoles={["company"]}>
              <MyAccount />
            </ProtectedRoute>
          }
        />
        <Route
          path="/help"
          element={
            <ProtectedRoute allowedRoles={["company", "technician"]}>
              <HelpSupport />
            </ProtectedRoute>
          }
        />

        {/* Public legal pages */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/legal" element={<LegalNotice />} />

        {/* Default redirect */}
        <Route
          path="/"
          element={
            user ? (
              user.role === "admin" ? (
                <Navigate to="/admin" replace />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {user && user.role !== "admin" && <MobileNav />}
      <LegalFooter />
      <Toaster position="top-center" richColors />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <OfflineProvider>
          <AppContent />
        </OfflineProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
